import { noop, type Result, unreachable } from "@mdotm/utils/runtime";
import type { UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";

export function useStaticQueryResultFrom<TOk, TErr>(result: Result<TOk, TErr>): UseQueryResult<TOk, TErr> {
	return useState(staticQueryResultFrom(result))[0];
}

export function staticQueryResultFrom<TOk, TErr>(result: Result<TOk, TErr>): UseQueryResult<TOk, TErr> {
	return result.isOk()
		? {
			dataUpdatedAt: 1767781922300, // random but static timestamp
			error: null,
			errorUpdateCount: 0,
			errorUpdatedAt: 1767781922300,
			failureCount: 0,
			failureReason: null,
			fetchStatus: "idle",
			isError: false,
			isLoading: false,
			isLoadingError: false,
			isRefetchError: false,
			isSuccess: true,
			status: "success",
			data: result.unwrap(),
			isFetched: true,
			isFetchedAfterMount: false,
			isFetching: false,
			isInitialLoading: false,
			isPaused: false,
			isPlaceholderData: false,
			isPreviousData: false,
			isRefetching: false,
			isStale: false,
			refetch() {
				return Promise.resolve(this);
			},
			remove: noop,
		}
		: result.isErr()
			? {
				dataUpdatedAt: 1767781922300, // random but static timestamp
				error: result.unwrapErr(),
				errorUpdateCount: 1,
				errorUpdatedAt: 1767781922300,
				failureCount: 1,
				failureReason: null,
				fetchStatus: "idle",
				isError: true,
				isLoading: false,
				isLoadingError: false,
				isRefetchError: true,
				isSuccess: false,
				status: "error",
				data: undefined!,
				isFetched: true,
				isFetchedAfterMount: false,
				isFetching: false,
				isInitialLoading: false,
				isPaused: false,
				isPlaceholderData: false,
				isPreviousData: false,
				isRefetching: false,
				isStale: false,
				refetch(opts) {
					return opts?.throwOnError ? Promise.reject(result.unwrapErr()) : Promise.resolve(this);
				},
				remove: noop,
			}
			: unreachable();
}
